import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PillButton, RainbowButton } from "@/components/RainbowButton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Flag, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { toast } from "sonner";

const REASONS = [
  { value: "spam", label: "Spam or misleading" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate_speech", label: "Hate speech or discrimination" },
  { value: "violence", label: "Violence or dangerous behaviour" },
  { value: "sexual_content", label: "Sexual or explicit content" },
  { value: "self_harm", label: "Self-harm or suicide" },
  { value: "impersonation", label: "Impersonation" },
  { value: "other", label: "Something else" },
];

/**
 * Report modal. Works for posts, users and replies.
 * @param {{ open, onClose, targetType: "post"|"user"|"reply", targetId, targetLabel?: string }} props
 */
export default function ReportModal({ open, onClose, targetType, targetId, targetLabel }) {
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setLoading(true);
    setErr("");
    try {
      await api.post(EP.submitReport, {
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details.trim() || undefined,
      });
      toast.success("Report submitted. Thank you for keeping BrontoY safe.");
      onClose?.();
      setReason("spam");
      setDetails("");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && onClose ? onClose() : null)}>
      <DialogContent className="max-w-lg rounded-3xl border-border bg-background p-0 overflow-hidden" data-testid="report-modal">
        <div className="p-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
            <Flag className="w-3 h-3" strokeWidth={2} />
            Report {targetType}
          </div>
          <DialogHeader className="p-0">
            <DialogTitle className="font-display text-2xl md:text-3xl font-medium tracking-tighter">
              Report this {targetType}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm mt-2 leading-relaxed">
              Reports are private. Our safety team reviews each one against the Community Guidelines.
            </DialogDescription>
          </DialogHeader>

          {targetLabel && (
            <div className="mt-5 rounded-2xl border border-border p-3 text-sm text-muted-foreground line-clamp-2">
              {targetLabel}
            </div>
          )}

          <div className="mt-6">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">Reason</Label>
            <RadioGroup value={reason} onValueChange={setReason} className="grid gap-2">
              {REASONS.map((r) => (
                <label
                  key={r.value}
                  htmlFor={`reason-${r.value}`}
                  className={`flex items-center gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${
                    reason === r.value ? "border-foreground bg-foreground/5" : "border-border hover:border-foreground/40"
                  }`}
                  data-testid={`report-reason-${r.value}`}
                >
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="mt-6">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Additional details (optional)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Anything else our team should know?"
              className="rounded-2xl border-border bg-transparent resize-none"
              data-testid="report-details-input"
            />
            <p className="text-xs text-muted-foreground text-right mt-1">{details.length}/500</p>
          </div>

          {err && (
            <div className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/10 text-destructive text-sm px-4 py-3" data-testid="report-error">
              {err}
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <PillButton variant="ghost" className="h-11" onClick={onClose} data-testid="report-cancel">Cancel</PillButton>
            <RainbowButton className="h-11" disabled={loading} onClick={submit} data-testid="report-submit">
              <span className="inline-flex items-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit report
              </span>
            </RainbowButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
